import { type ComponentPropsWithoutRef, type MouseEvent, type ReactNode } from 'react';

import {
  ExitBadge,
  InvestStatusBadge,
  toExitBadgeType,
  toInvestStatusBadgeType,
} from '@entities/investment/@x/portfolio';
import { cn } from '@shared/lib/cn';
import { EMPTY_PLACEHOLDER } from '@shared/lib/format';
import { Sparkline, type SparklinePoint } from '@shared/ui/chart';
import { Icon } from '@shared/ui/icon';
import { TABLE_HOVER_ATTR, TABLE_HOVER_CELL_CLASS } from '@shared/ui/table';

import { PORTFOLIO_ROW_TEXT } from '../config/portfolio.text';
import {
  PORTFOLIO_ROW_CELL_BORDER_CLASS,
  PORTFOLIO_ROW_CELL_CLASS,
  PORTFOLIO_ROW_HEADER_CELL_CLASS,
  PORTFOLIO_ROW_ITEM_CLASS,
  PORTFOLIO_ROW_LINE_HEIGHT,
  PORTFOLIO_ROW_LINK_CLASS,
  PORTFOLIO_ROW_MIN_WIDTH_PX,
  PORTFOLIO_ROW_SCOPE_COLUMNS,
  PORTFOLIO_ROW_STACK_CLASS,
  PORTFOLIO_ROW_STACK_LINE_CLASS,
  PORTFOLIO_ROW_STACK_LINE_HEIGHT,
  PORTFOLIO_ROW_TABLE_CLASS,
  type PortfolioRowColumnKey,
  portfolioRowColumnWidth,
  type PortfolioRowScope,
} from '../config/portfolio-row.constant';
import {
  formatPortfolioAmount,
  formatPortfolioDate,
  formatPortfolioIrr,
  formatPortfolioMultiple,
} from '../lib/portfolio-format';
import { type InvestStatus } from '../model/cross-types';
import { type HistoryPoint, type PortfolioListItem } from '../model/portfolio-list';

export type PortfolioRowProps =
  | { kind: 'header'; scope: 'all' | 'fund'; continued?: false }
  | {
      kind: 'first';
      scope: 'all' | 'fund';
      continued?: boolean;
      item: PortfolioListItem;
      investStatus: InvestStatus;
      /**
       * 병합 칸(회사명 · 담당심사역 · 매출 · 기업가치 그래프)의 rowSpan = 그 회사의 줄 수(DD-22 3, 기존 서비스와 같다).
       * 2 이상이면 이어지는 next 줄에 `spanned` 를 준다. 병합 칸은 묶음 끝까지 덮으므로 아래 선은 늘 `line` 이다
       */
      rowSpan?: number;
      onOpenPortfolio?: (item: PortfolioListItem) => void;
      onOpenFund?: (item: PortfolioListItem) => void;
    }
  | {
      kind: 'next';
      scope: 'all';
      continued?: boolean;
      /**
       * 앞 first 줄의 rowSpan 이 병합 칸을 덮는다 — 그 칸(`<td>`)을 그리지 않는다(표 안, DD-22 3).
       * 없으면 Figma variant 처럼 병합 칸 자리를 빈 칸으로 그린다(견본 · 한 줄만 볼 때)
       */
      spanned?: boolean;
      item: PortfolioListItem;
      onOpenFund?: (item: PortfolioListItem) => void;
    };

type BodyProps = Exclude<PortfolioRowProps, { kind: 'header' }>;

const toSparkline = (points: HistoryPoint[]): SparklinePoint[] =>
  points.map((point) => ({ date: point.when, amount: point.amount }));

/**
 * 칸의 값 줄들: 한 줄이면 칸 가운데, 여러 줄이면 줄마다 한 줄 자리(54)씩 위에서부터(칸은 align-top).
 * 평가/회수금액 칸은 값 옆에 ExitBadge 가 붙어 값이 ReactNode 다
 */
function Lines({ values }: { values: ReactNode[] }) {
  if (values.length <= 1) return <div className={PORTFOLIO_ROW_ITEM_CLASS}>{values[0]}</div>;
  return (
    <div data-lines={values.length} className={PORTFOLIO_ROW_STACK_CLASS}>
      {values.map((value, index) => {
        const at = index === 0 ? 'first' : 'next';
        return (
          <div
            // 값은 순서로만 구분된다(같은 값이 겹칠 수 있다)
            key={index}
            className={PORTFOLIO_ROW_STACK_LINE_CLASS[at]}
            style={{ height: PORTFOLIO_ROW_STACK_LINE_HEIGHT[at] }}
          >
            {value}
          </div>
        );
      })}
    </div>
  );
}

function HeaderRow({ scope }: { scope: 'all' | 'fund' }) {
  return (
    <tr data-kind="header" data-scope={scope}>
      {PORTFOLIO_ROW_SCOPE_COLUMNS[scope].map((column) => (
        <th
          key={column.key}
          scope="col"
          style={{ width: portfolioRowColumnWidth(scope, column.px) }}
          className={PORTFOLIO_ROW_HEADER_CELL_CLASS}
        >
          <span className="flex items-center justify-between">
            {PORTFOLIO_ROW_TEXT.header[column.key]}
            {/* 정렬 · 너비 조절이 들어올 자리(기능 추가 예정) */}
            <Icon name="sort" size="xs" className="text-fg-muted" />
          </span>
        </th>
      ))}
    </tr>
  );
}

function BodyRow(props: BodyProps) {
  const { kind, scope, continued = false, item } = props;
  const first = props.kind === 'first' ? props : null;
  const rowSpan = first?.rowSpan && first.rowSpan > 1 ? first.rowSpan : undefined;
  const spanned = props.kind === 'next' && props.spanned === true;
  const columns = PORTFOLIO_ROW_SCOPE_COLUMNS[scope].filter(
    (column) => !(spanned && column.merged),
  );
  const openFund = () => props.onOpenFund?.(item);

  const showValuation = item.status !== 'CompleteExit';
  const investLines = item.investD3History.map((invest) => ({
    at: formatPortfolioDate(invest.investAt),
    volume: formatPortfolioAmount(invest.investVolume, invest.exchangeUnit),
  }));
  const valuationLines: { key: string; amount: ReactNode; at: string }[] = [
    ...(showValuation
      ? [
          {
            key: 'valuation',
            amount: formatPortfolioAmount(
              item.remainValuation * item.remainValuationExchangeRate,
              'KRW',
            ),
            at: formatPortfolioDate(item.valuatedAt),
          },
        ]
      : []),
    ...item.exitD3History.map((exit) => {
      const badge = toExitBadgeType(exit.exitType);
      return {
        key: exit.id,
        amount: (
          <>
            {formatPortfolioAmount(exit.exitVolume * exit.exchangeRate, 'KRW')}
            {badge ? <ExitBadge type={badge} /> : null}
          </>
        ),
        at: formatPortfolioDate(exit.exitAt),
      };
    }),
  ];
  const lineCount = Math.max(1, investLines.length, valuationLines.length);
  const statusBadge = first ? toInvestStatusBadgeType(first.investStatus) : null;

  // 회사명 · 펀드명은 칸 전체가 이동(기존 서비스 — 글자 밖을 눌러도). 버튼은 키보드 · 읽기 프로그램용이고
  // 누르면 클릭이 칸으로 올라가 칸이 한 번 처리한다
  const cellAction: Partial<Record<PortfolioRowColumnKey, () => void>> = {
    ...(first?.onOpenPortfolio ? { portfolioName: () => first.onOpenPortfolio?.(item) } : {}),
    ...(props.onOpenFund ? { fundName: openFund } : {}),
  };
  const fundButton = (
    <button type="button" className={PORTFOLIO_ROW_LINK_CLASS}>
      {item.fundName}
    </button>
  );

  // 여러 줄 칸은 위에서부터 줄 자리를 채운다(align-top) — 칸마다 줄이 같은 높이에 맞는다
  const stacked: Partial<Record<PortfolioRowColumnKey, boolean>> = {
    investAt: investLines.length > 1,
    investVolume: investLines.length > 1,
    valuation: valuationLines.length > 1,
    valuatedAt: valuationLines.length > 1,
  };

  const cells: Record<PortfolioRowColumnKey, ReactNode> = {
    portfolioName: first ? (
      <div className={PORTFOLIO_ROW_ITEM_CLASS}>
        <button type="button" className={PORTFOLIO_ROW_LINK_CLASS}>
          {item.portfolioName}
        </button>
        {statusBadge ? <InvestStatusBadge type={statusBadge} /> : null}
      </div>
    ) : null,
    fundName: <div className={PORTFOLIO_ROW_ITEM_CLASS}>{fundButton}</div>,
    investAt: <Lines values={investLines.map((line) => line.at)} />,
    investVolume: <Lines values={investLines.map((line) => line.volume)} />,
    valuation: <Lines values={valuationLines.map((line) => line.amount)} />,
    valuatedAt: <Lines values={valuationLines.map((line) => line.at)} />,
    irr: <div className={PORTFOLIO_ROW_ITEM_CLASS}>{formatPortfolioIrr(item.irr)}</div>,
    multiple: (
      <div className={PORTFOLIO_ROW_ITEM_CLASS}>{formatPortfolioMultiple(item.multiple)}</div>
    ),
    internalManagerName: first ? (
      <div className={PORTFOLIO_ROW_ITEM_CLASS}>
        {item.internalManagerName ?? EMPTY_PLACEHOLDER}
      </div>
    ) : null,
    revenueHistory: first ? (
      <div className="flex justify-center">
        <Sparkline
          points={toSparkline(item.revenueHistory)}
          subject={PORTFOLIO_ROW_TEXT.header.revenueHistory}
        />
      </div>
    ) : null,
    stockPriceHistory: first ? (
      <div className="flex justify-center">
        <Sparkline
          points={toSparkline(item.stockPriceHistory)}
          subject={PORTFOLIO_ROW_TEXT.header.stockPriceHistory}
        />
      </div>
    ) : null,
  };

  // hover(DD-26 6): 병합 칸 → 회사 묶음(PortfolioRowGroup) 전체, 보통 칸 → 그 펀드 줄의 보통 칸(줄 안에 쌓인 투자 여러 건 포함).
  // 펀드 안 목록(scope=fund)은 병합이 없어 어느 칸이든 그 줄 전체
  const hoverClass = (merged: boolean) =>
    scope === 'fund'
      ? TABLE_HOVER_CELL_CLASS.row
      : merged
        ? TABLE_HOVER_CELL_CLASS.group
        : TABLE_HOVER_CELL_CLASS.rowAndGroup;

  return (
    <tr
      {...TABLE_HOVER_ATTR.row}
      data-kind={kind}
      data-scope={scope}
      data-continued={continued}
      data-spanned={spanned || undefined}
      style={{ height: `calc(${PORTFOLIO_ROW_LINE_HEIGHT} * ${lineCount})` }}
      className={kind === 'next' ? 'cursor-pointer' : undefined}
      onClick={kind === 'next' ? openFund : undefined}
    >
      {columns.map((column) => {
        const action = cellAction[column.key];
        return (
          <td
            key={column.key}
            onClick={
              action
                ? (event: MouseEvent) => {
                    // next 줄은 줄 전체가 펀드로 간다 — 두 번 부르지 않게 줄로 올리지 않는다
                    event.stopPropagation();
                    action();
                  }
                : undefined
            }
            {...(scope === 'all' && column.merged ? TABLE_HOVER_ATTR.merged : {})}
            rowSpan={column.merged ? rowSpan : undefined}
            style={{ width: portfolioRowColumnWidth(scope, column.px) }}
            className={cn(
              PORTFOLIO_ROW_CELL_CLASS,
              column.merged && continued && rowSpan === undefined
                ? PORTFOLIO_ROW_CELL_BORDER_CLASS.continued
                : PORTFOLIO_ROW_CELL_BORDER_CLASS.line,
              hoverClass(column.merged),
              action && 'cursor-pointer',
              stacked[column.key] && 'align-top',
            )}
          >
            {cells[column.key]}
          </td>
        );
      })}
    </tr>
  );
}

/**
 * 포트폴리오 목록 표의 한 줄(`<tr>`): 머리 줄 · 회사의 첫 줄 · 같은 회사의 다음 펀드 줄 · 펀드 안 목록의 줄.
 * `PortfolioRowTable` 의 `<thead>` · `<tbody>` 안에 놓는다. 칸 너비는 머리 줄이 정한다(비율 + 최소 폭, DD-15 H2).
 */
export function PortfolioRow(props: PortfolioRowProps) {
  if (props.kind === 'header') return <HeaderRow scope={props.scope} />;
  return <BodyRow {...props} />;
}

export type PortfolioRowGroupProps = ComponentPropsWithoutRef<'tbody'>;

/**
 * 한 회사의 줄 묶음(`<tbody>`): first 줄 + 이어지는 next 줄. 병합 칸에 올리거나 포커스하면 묶음 전체가 hover 면(DD-26 6).
 * `PortfolioRowTable` 안에 회사마다 하나씩 놓는다(표 하나에 tbody 여러 개)
 */
export function PortfolioRowGroup(props: PortfolioRowGroupProps) {
  return <tbody {...TABLE_HOVER_ATTR.group} {...props} />;
}

export interface PortfolioRowTableProps extends Omit<ComponentPropsWithoutRef<'table'>, 'style'> {
  scope: PortfolioRowScope;
}

/**
 * PortfolioRow 를 담는 표: 부모 폭을 채우고(DD-15 H2) 칸은 Figma 최소 폭의 비율로 나눈다.
 * 칸 최소 폭의 합(all 1315 · fund 1121)보다 좁아지지 않으므로 부모가 가로 스크롤을 맡는다.
 */
export function PortfolioRowTable({ scope, className, ...rest }: PortfolioRowTableProps) {
  return (
    <table
      {...rest}
      data-scope={scope}
      style={{ minWidth: PORTFOLIO_ROW_MIN_WIDTH_PX[scope] }}
      className={cn(PORTFOLIO_ROW_TABLE_CLASS, className)}
    />
  );
}
